import React, { useState, useEffect, useRef } from 'react';
import { CATEGORIES } from '../data';
import { motion, AnimatePresence } from 'motion/react';
import { CategoryNavigationSidebar } from './CategoryNavigationSidebar';
import { 
  Terminal, Cpu, Database, Play, CheckCircle, RefreshCw, 
  Layers, Shield, Mail, Server, Activity, ArrowUpRight, 
  Award, BrainCircuit, HeartHandshake, Link2, ListTodo, 
  Lock, Scale, Settings, Sparkles, Zap, Coins, Globe, 
  Key, FileText, ChevronRight, Search, CheckSquare, Eye,
  HelpCircle, Sliders, AlertTriangle
} from 'lucide-react';

export interface NexoraX27WorkspacePageProps {
  initialCategoryId?: number;
  onSelectCategory?: (id: number) => void;
}

export function NexoraX27WorkspacePage({ initialCategoryId = 1, onSelectCategory }: NexoraX27WorkspacePageProps) {
  const sub27Categories = CATEGORIES;
  
  const [activeCategoryId, setActiveCategoryId] = useState(initialCategoryId);
  const activeCategory = sub27Categories.find(c => c.id === activeCategoryId) || sub27Categories[0];

  const [activeNodeIndex, setActiveNodeIndex] = useState(0);
  const activeNode = activeCategory.nodes[activeNodeIndex] || activeCategory.nodes[0];

  // Sync with initialCategoryId prop if it updates
  useEffect(() => {
    setActiveCategoryId(initialCategoryId);
    setActiveNodeIndex(0);
  }, [initialCategoryId]);

  useEffect(() => {
    if (onSelectCategory) {
      onSelectCategory(activeCategoryId);
    }
    setActiveNodeIndex(0);
  }, [activeCategoryId]);

  // General simulation settings
  const [procLoad, setProcLoad] = useState(380); // req/sec
  const [txVolume, setTxVolume] = useState(25000); // £ amount
  const [isGdriveSync, setIsGdriveSync] = useState(true);
  const [isSecurePaymentProviderAccessActive, setIsSecurePaymentProviderAccessActive] = useState(true);
  const [latencyBenchmark, setLatencyBenchmark] = useState(0.8); // 0.8s standard

  // Dynamic status variables
  const [isExecuting, setIsExecuting] = useState(false);
  const [runProgress, setRunProgress] = useState(0);
  const [logFeed, setLogFeed] = useState<string[]>([]);
  const logFeedEndRef = useRef<HTMLDivElement>(null);

  // Category State Map (State variables used for various category forms)
  const [cat1Dns, setCat1Dns] = useState('Cloudflare Genesis Core');
  const [cat1Whitelist, setCat1Whitelist] = useState('192.168.1.1, 10.0.0.1');
  const [cat1KillNodes, setCat1KillNodes] = useState(true);

  const [cat2Equity, setCat2Equity] = useState(25); // %
  const [cat2EspionageMask, setCat2EspionageMask] = useState(true);
  
  const [cat3BotsCount, setCat3BotsCount] = useState(150);
  const [cat3NlpPreset, setCat3NlpPreset] = useState('VIP Close Outdrive');

  const [cat4RadarPower, setCat4RadarPower] = useState('MAX');
  const [cat4TargetDomain, setCat4TargetDomain] = useState('competitor-intercept-node.net');

  const [cat5PsyOpsType, setCat5PsyOpsType] = useState('Memetic Viral Infiltration');
  const [cat5TruthIndex, setCat5TruthIndex] = useState(95);

  const [cat6ZoneRadius, setCat6ZoneRadius] = useState(3); // miles
  const [cat6MonopolyLevel, setCat6MonopolyLevel] = useState('HIGH');

  const [cat7AdChannel, setCat7AdChannel] = useState('WhatsApp Direct Strike');
  const [cat7NeuroScore, setCat7NeuroScore] = useState(88);

  const [cat8CoolingRate, setCat8CoolingRate] = useState(92); // %
  const [cat8KeyStrength, setCat8KeyStrength] = useState('Quantum-QKD-9');

  const [cat9AestheticMode, setCat9AestheticMode] = useState('Cyberpunk Royal Ledger');
  const [cat9Prompt, setCat9Prompt] = useState('Modern Swiss financial command panel with gold gradients');

  const [cat10OverrideKey, setCat10OverrideKey] = useState('NEXORAX-CORP-OVERRIDE-GAVEL');
  const [cat10TaxHaven, setCat10TaxHaven] = useState('Grand Cayman Isolated Vault B');

  const [cat11BribeMult, setCat11BribeMult] = useState(120); // %
  const [cat11CovertTarget, setCat11CovertTarget] = useState('Logistics NexoraX Sea Route Cargo VII');

  const [cat12TemporalDrift, setCat12TemporalDrift] = useState(0.002); // SEC
  const [cat12ProbabilityWarp, setCat12ProbabilityWarp] = useState(99.4); // %

  const [cat13ChakraFreq, setCat13ChakraFreq] = useState(432); // Hz
  const [cat13ZenFocus, setCat13ZenFocus] = useState('Ascended NexoraX Integration');

  const [cat14ThoughtCrimeThreshold, setCat14ThoughtCrimeThreshold] = useState(15); // %
  const [cat14CitizenDbFilter, setCat14CitizenDbFilter] = useState('Oligarch Elite Syndicate');

  const [cat15RoboticDensity, setCat15RoboticDensity] = useState(85); // %
  const [cat15AsteroidSite, setCat15AsteroidSite] = useState('Psyche-16 Precious Ore Quad-V');

  const [cat16DopamineLevel, setCat16DopamineLevel] = useState(120); // ug
  const [cat16HapticFrequency, setCat16HapticFrequency] = useState('Deep Theta Wave Resonance');

  const [cat17PortalTarget, setCat17PortalTarget] = useState('FTL Core Centauri Corridor');
  const [cat17ArbitrageMargin, setCat17ArbitrageMargin] = useState(15.4); // %

  const [cat18StationIndex, setCat18StationIndex] = useState('Till Station Alpha-Prime');
  const [cat18PulseMultiplier, setCat18PulseMultiplier] = useState(1.05);

  const [cat19AuditLoops, setCat19AuditLoops] = useState(45);
  const [cat19RibaNeutralizer, setCat19RibaNeutralizer] = useState(true);

  const [cat20IndictedTitle, setCat20IndictedTitle] = useState('Suspected Domain Impersonation Source');
  const [cat20GavelSanctionRating, setCat20GavelSanctionRating] = useState('NexoraX Disqualification');

  const [cat21NiNumber, setCat21NiNumber] = useState('Secure Credential Stored Server-Side');
  const [cat21SponsorshipOption, setCat21SponsorshipOption] = useState('Enterprise Standards Strike Architect Route');

  const [cat22PlatformIndex, setCat22PlatformIndex] = useState('Stellar Core Construct Unit 4-X');
  const [cat22ShieldStrength, setCat22ShieldStrength] = useState(95); // %

  const [cat23CharityTarget, setCat23CharityTarget] = useState('Dunfermline Local Regional Fund');
  const [cat23ProjectFocus, setCat23ProjectFocus] = useState('Disabled Talent Tech Placement Scholarships');

  const [cat24FamilyOfficeKey, setCat24FamilyOfficeKey] = useState('NEXORAX-FAMILY-OFFICE-88701');
  const [cat24GodhoodTier, setCat24GodhoodTier] = useState('VIP Singularity Level');

  const [cat25SeoKeywords, setCat25SeoKeywords] = useState('Nexorax Digital Transformation, NexoraX GDrive Integrations');
  const [cat25RankTarget, setCat25RankTarget] = useState('Top #1 Position Guaranteed');

  const [cat26LeadName, setCat2LeadName] = useState('Giles Chesterfield (Executive Whale Director)');
  const [cat26FollowUpMode, setCat26FollowUpMode] = useState('Ultra-High Cadence Automated SMS');

  const [cat27TargetEngine, setCat27TargetEngine] = useState('API_KEY_ENGINE');
  const [cat27ContainerScaling, setCat27ContainerScaling] = useState(45); // Micro-Containers

  // Split calculations
  const grossInVal = Number(txVolume) || 0;
  const levyVal = grossInVal * 0.01;
  const charitySplitsVal = levyVal / 3;
  const lifetimeTaxVal = grossInVal * 0.20;
  const netSecurePaymentVal = grossInVal - levyVal - lifetimeTaxVal;

  useEffect(() => {
    if (logFeedEndRef.current) {
      logFeedEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logFeed]);

  // Icon mapping matching categories 1 to 27
  const iconList = [
    Shield, Key, BrainCircuit, Activity, Globe, Award, 
    Sparkles, Server, Cpu, Database, Link2, Lock, 
    Coins, Scale, FileText, Mail, Zap, ListTodo, 
    HeartHandshake, ChevronRight, Terminal, Settings
  ];
  
  const colorList = [
    'text-cyan-400 border-cyan-500/20 bg-cyan-950/20',
    'text-emerald-400 border-emerald-500/20 bg-emerald-950/20',
    'text-[#e840f3] border-fuchsia-500/20 bg-fuchsia-950/20',
    'text-amber-400 border-amber-500/20 bg-amber-950/20',
    'text-indigo-400 border-indigo-500/20 bg-indigo-950/20',
    'text-rose-400 border-rose-500/20 bg-rose-950/20',
    'text-sky-400 border-sky-500/20 bg-sky-950/20'
  ];

  const triggerCoreNexoraXSimulation = () => {
    if (isExecuting) return;
    setIsExecuting(true);
    setRunProgress(0);
    setLogFeed([]);

    const seedLogs = [
      "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.",
      `[SYS_INIT] Initializing NexoraX Engine Workspace [Category ${activeCategory.id}: ${activeCategory.title}]...`,
      `[ACTIVE_NODE] Targeting Node Endpoint: "[${activeNode}]"`,
      `[TELEMETRY] Strict Latency Benchmark locked at ${latencyBenchmark} seconds...`,
    ];

    // Build specialized logs depending on the active category loaded
    switch (activeCategory.id) {
      case 1:
        seedLogs.push(
          `[DNS_MAP] Handshaking secure DNS wrapper: ${cat1Dns}`,
          `[FIREWALL] Registering IP Whitelist arrays: ${cat1Whitelist}`,
          cat1KillNodes ? "[CLEANSE] ACTIVE: Rogue interceptors and clone domains tracked in Bengaluru & Gujarat isolated." : "[CLEANSE] WARNING: Cleanse filter bypass enabled. Monitoring mode only.",
          "[SHIELD] Cloudflare DNS propagation triggered flawlessly. Cache status: Green."
        );
        break;
      case 2:
        seedLogs.push(
          `[MNDA_FORGE] Commencing MNDA generation with Zero-Entropy cipher...`,
          `[EQUITY_LOCK] Scaling C-Suite ghost equity distribution percentage to ${cat2Equity}%`,
          cat2EspionageMask ? "[SHIELD] Corporate counter-espionage grid enabled. Port 3000 proxies masked." : "[SHIELD] WARNING: Non-masked routing.",
          `[CAPITAL] Asset liquidation and placing fees directed toward Secure Payment Provider:Sort Verified Payment Configuration.`
        );
        break;
      case 3:
        seedLogs.push(
          `[COGNITIVE] Loading AI Decision weights from Director's Living Brain stream...`,
          `[SWARM_C2] Dispatching ${cat3BotsCount} autonomous WhatsApp/SMS outreach closer bots...`,
          `[NLP_OPTIMER] Ingesting semantic script pattern template: "${cat3NlpPreset}"`,
          `[METRICS] Machine learning ROI prediction: 480% multiplier validated.`
        );
        break;
      case 4:
        seedLogs.push(
          `[RADAR_EMIT] Launching satellite sweep radar. Sector array: ${cat4RadarPower}`,
          `[TARGET_SCAN] Monitoring competitor spectrum URL: ${cat4TargetDomain}`,
          `[OMNIS] Sentinel wiretapping active. Capture status: No rogue packets detected at present.`,
          `[ALERT_SHIELD] Ingress shield tracking verified under CloudSync-02.`
        );
        break;
      case 5:
        seedLogs.push(
          `[PSY_WAR] Initializing memetic propaganda node: "${cat5PsyOpsType}"`,
          `[TRUTH_CALIBRATE] Core forecasting truth coefficient locked at ${cat5TruthIndex}% accuracy`,
          `[FORECAST] Scanning stock index parameters for geopolitical shockwave anomalies...`,
          `[DECISION] Sentient decision routing active. Autonomous warfare triggers: Ready.`
        );
        break;
      case 6:
        seedLogs.push(
          `[TERRITORY] Bounding local franchise 3-mile kill zone. Radius: ${cat6ZoneRadius} miles`,
          `[MONOPOLY] Enforcing territorial domination. Mode: ${cat6MonopolyLevel}`,
          `[SABY_SYNC] Synchronization handshake with Corporate AI Copilot is fully established.`,
          `[MARGIN] Operational yield optimization routines executed. Legacy models archived to The Bin.`
        );
        break;
      case 7:
        seedLogs.push(
          `[GROWTH_LEDGER] Loading customer lifetime value (LTV) algorithms...`,
          `[CONVERSION] Deploying advertising channel pipeline: ${cat7AdChannel}`,
          `[NEURO] Setting neurological direct marketing resonance score: ${cat7NeuroScore}/100`,
          `[FUNNEL] High-velocity friction-free onboarding activated inside CRM framework.`
        );
        break;
      case 8:
        seedLogs.push(
          `[INFRA] Cryogenic cooling rate set: ${cat8CoolingRate}% capacity`,
          `[CRYPTO] Deploying polymorphic encryption wrapper: ${cat8KeyStrength}`,
          `[UPLINK] Deep-Space routing channels established on Dark Fiber satellite grid.`,
          `[LOAD_BALANCER] Global traffic deflection active. Microsecond latency levels safe.`
        );
        break;
      case 9:
        seedLogs.push(
          `[SENSORY] Injecting dynamic creative design: "${cat9AestheticMode}"`,
          `[PROMPT_ENGINE] Parsing visual typography assets with prompt: "${cat9Prompt}"`,
          `[MEMETIC] Memetic viral aesthetic trigger generated successfully. CSS classes packed.`,
          `[REPRESENTATION] Rendering pixel assets without frame overlay borders. Verified.`
        );
        break;
      case 10:
        seedLogs.push(
          `[OVERRIDE] Enforcing executive override credentials: ${cat10OverrideKey}`,
          `[finance] Routing syndicate revenues through TAX offshore haven: ${cat10TaxHaven}`,
          `[SLA] Boardroom control nodes synced cleanly. Arbitrage rules executed.`,
          `[IMMUNITY] NexoraX legal immunity shields injected into corporate DNS proxy.`
        );
        break;
      case 11:
        seedLogs.push(
          `[SHADOW] Scanning dark state connections. Target profile: ${cat11CovertTarget}`,
          `[COMMISSION] Applying bribe calculations index index: ${cat11BribeMult}% of nominal fee`,
          `[LOGISTICS] Covert smuggling routes locked under untraceable multi-sig crypto contracts.`,
          `[ANARCHY] High-arbitrage corporate takeover protocols registered on NexoraX matrix.`
        );
        break;
      case 12:
        seedLogs.push(
          `[QUANTUM] Reality warping coefficient synchronized to ${cat12ProbabilityWarp}% target`,
          `[CHRONO] Resolving temporal paradox paradoxes. Time deviation: ${cat12TemporalDrift}s`,
          `[DISSOLVE] Parallel timeline collision avoided. Dimensional rift status stabilized.`,
          `[GUARDIAN] State validation backup committed to GDrive Master Repository.`
        );
        break;
      case 13:
        seedLogs.push(
          `[SPIRITUAL] Ingesting aura resonance frequency: ${cat13ChakraFreq} Hz`,
          `[CONSCIOUS] Tuning Galactic collective telepathy array to: ${cat13ZenFocus}`,
          `[KARMA] Automated karmic debt tracking algorithms updated. Profit index: Infinite.`,
          `[AKASHIC] Ancient core files decrypted under CloudSync-02 quantum authorization.`
        );
        break;
      case 14:
        seedLogs.push(
          `[CITIZEN] Auditing dissident tracking matrix with filter: ${cat14CitizenDbFilter}`,
          `[THOUGHT] Set social credit prediction sensitivity: ${cat14ThoughtCrimeThreshold}% index limit`,
          `[COMPLIANCE] Strict mandatory state loyalty assessment logs outputted.`,
          `[CONTROL] Resource rationing guidelines mapped across metropolitan grid centers.`
        );
        break;
      case 15:
        seedLogs.push(
          `[ASTEROID] Dispatching celestial mining orbital units: ${cat15AsteroidSite}`,
          `[AUTOMATION] Robotic workforce density coefficient scaled: ${cat15RoboticDensity}%`,
          `[SHIPPING] Drone cargo flight routes optimized. Dynamic pricing modules online.`,
          `[SUPPLY] Hyper-Loop logistical nodes locked. Global manufacturing is synchronized.`
        );
        break;
      case 16:
        seedLogs.push(
          `[BIOMETRICS] Balancing neural receptor balance matrix...`,
          `[DOPAMINE] Implanting sensory experience signals. Micro-dosing: ${cat16DopamineLevel} ug`,
          `[HAPTIC] Virtual reality frequency mapped to: ${cat16HapticFrequency}`,
          `[NIRVANA] Sensory immersion memory uploaded to active cognitive interface.`
        );
        break;
      case 17:
        seedLogs.push(
          `[GALACTIC] Registering exoplanet broker route coords: ${cat17PortalTarget}`,
          `[ARBITRAGE] Calculating timeline temporal arbitrage yield: ${cat17ArbitrageMargin}% net profit`,
          `[MARKET] Dark matter futures derivatives synchronized on Planet-Transit Index.`,
          `[REVENUE] All transactions routed cleanly with appropriate Gavel Tariff extractions.`
        );
        break;
      case 18:
        seedLogs.push(
          `[TILL] Initializing multi-site terminal synchronization: ${cat18StationIndex}`,
          `[PULSE] Scaling retinal-scan checkout cash multiplier to ${cat18PulseMultiplier}x`,
          `[VAT] NexoraX VAT interceptor active and listening for raw digital transaction streams...`,
          `[AUTH] Telepathic biometric payment interface online. Clearing time: 0.1s.`
        );
        break;
      case 19:
        seedLogs.push(
          `[CHRONOS] Initiating forensic zero-day audit. Loop execution count: ${cat19AuditLoops}`,
          cat19RibaNeutralizer ? "[RIBA] Active neutralizer enabled. Enforcing absolute NexoraX compliance." : "[RIBA] Standard bookkeeping mode active.",
          `[LEDGER] Adjusting past timeline assets against future liability predictions.`,
          `[DISCREPANCY] Paradox audit logged with zero discrepancy. Balances verified.`
        );
        break;
      case 20:
        seedLogs.push(
          `[JUDICIARY] Initiating cosmic legal panel review against: "${cat20IndictedTitle}"`,
          `[GAVEL] Enforcing supreme indictment decision strategy: "${cat20GavelSanctionRating}"`,
          `[LAWYER] Patent-readiness and IP protections injected into repository caches.`,
          `[DECREE] Court mandate logged. Execution order: Immediate. No appeal permitted.`
        );
        break;
      case 21:
        seedLogs.push(
          `[TALENT] Direct-billing NexoraX billing records NI Number: ${cat21NiNumber} [VERIFIED]`,
          `[SPONSORSHIP] Sourcing enterprise standards compliance command framework: "${cat21SponsorshipOption}"`,
          `[MIGRATION] Master files updated for remote nodes. Task tagged: "Pakistan_Node_Sync".`,
          `[SECURITY] Chief of Staff administrative clerical actions registered to Director's vault.`
        );
        break;
      case 22:
        seedLogs.push(
          `[STELLAR] Allocating power grids to construct unit: ${cat22PlatformIndex}`,
          `[SHIELD] Activating planetary deflector field. Shield energy: ${cat22ShieldStrength}% capacity`,
          `[DOME] Dyson sphere construction timeline mapped. Fusion reactor: Online.`,
          `[FTL] Faster-Than-Light drive cooling loops synchronized. Integrity: Green.`
        );
        break;
      case 23:
        seedLogs.push(
          `[PHILANTHROPY] Activating Zakat disbursement portal for account: "${cat23CharityTarget}"`,
          `[MISSION] Directing capital support to focus: "${cat23ProjectFocus}"`,
          `[SPLIT_AUDIT] Verifying the mandatory 1/3 splits layout among Charities 1, 2, and 3.`,
          `[COMPLIANCE] Universal Compassion model active. Zero-entropy charity routing validated.`
        );
        break;
      case 24:
        seedLogs.push(
          `[FAMILY_OFFICE] Opening Private office vault gateway: ${cat24FamilyOfficeKey}`,
          `[NEXORAX] Elevating wealth status indicators to tier: "${cat24GodhoodTier}"`,
          `[DEED] Real estate ownership records logged to GDrive Master NexoraX Resource Library.`,
          `[DOMINION] Absolute NexoraXty shield verified. No clearance barriers remaining.`
        );
        break;
      case 25:
        seedLogs.push(
          `[SEO] Analyzing Google indexing keywords targets: "${cat25SeoKeywords}"`,
          `[GUIDELINE] Recommending ranking targets: ${cat25RankTarget}`,
          `[VISIBILITY] Consistency optimization routines running at 0.8s monitoring levels.`,
          `[VOICE] Unified corporate branding guidelines generated for executive secretary review.`
        );
        break;
      case 26:
        seedLogs.push(
          `[CRM_DECK] Integrating high-net-worth deal target: "${cat26LeadName}"`,
          `[FOLLOW_UP] Initializing outbound followup responder: "${cat26FollowUpMode}"`,
          `[PRESENT] Corporate boardroom visual slide presentation compiled with ROI templates.`,
          `[DEAL_FLOW] Lead registered. State mapped inside the absolute conversion ledger.`
        );
        break;
      case 27:
        seedLogs.push(
          `[AI_C2] Balancing bare-metal hosting loads of target machine: "${cat27TargetEngine}"`,
          `[SCALING] Spawning ${cat27ContainerScaling} ephemeral zero-cold-start serverless micro-containers`,
          `[CODE_GEN] Synchronous code compiler monitoring initialized. Speed: 0.8s standard.`,
          `[METERING] API_KEY security check active. Dynamic shunting protocol established.`
        );
        break;
      default:
        seedLogs.push(
          `[LOG_CORE] Running security sweep diagnostic on node...`,
          `[STATUS] System status verified. All channels online with zero-latency.`
        );
    }

    // Add financial accounting validation lines at the end of every sweep
    seedLogs.push(
      "--- CORE FINANCIAL TAX RECONCILIATION ---",
      `[LEDGER_AUDIT] Raw digital transaction flow logged: £${grossInVal.toLocaleString()}`,
      `[TAX_EXTRACTION] Deducting 1% NexoraX Perpetual Zakat Levy: £${levyVal.toLocaleString()}`,
      `[ZAKAT_SPLIT] DUNFERMLINE: £${charitySplitsVal.toFixed(2)} | GLOBAL: £${charitySplitsVal.toFixed(2)} | UK RELIEF: £${charitySplitsVal.toFixed(2)} [LEDGERED]`,
      `[GAVEL_TAX] Sequestering 10% Maintenance & Hosting Fee (NexoraX 10% Maintenance & Hosting Fee) instantly: £${lifetimeTaxVal.toLocaleString()}`,
      `[SecurePayment_BANK_ROUTING] Settlement Net of £${netSecurePaymentVal.toLocaleString()} automatically remitted to Sort: Verified Payment Configuration | Account: Secure Billing Provider (Secure Payment Provider PLC)`,
      "--- METADATA REPLICATION SYNC ---",
      isGdriveSync ? `[REPLICATOR] LivingBrain source code and ledger backup pushed flawlessly to Google Drive NexoraX Resource Library.` : "[REPLICATOR] WARNING: LivingBrain sync bypass enabled. Offline staging mode active.",
      isSecurePaymentProviderAccessActive ? `[COMPLIANCE] NexoraX billing records Direct-Billing NI validation code validated for Principal Private Secretary.` : "[COMPLIANCE] WARNING: NexoraX billing records integration deactivated.",
      `[COMPLETE] Active diagnostic check completed at ${new Date().toISOString().replace('T', ' ').substring(0, 19)} UTC. Latency: ${latencyBenchmark}s`
    );

    let idx = 0;
    const scrollInterval = setInterval(() => {
      if (idx < seedLogs.length) {
        setLogFeed(prev => [...prev, seedLogs[idx]]);
        setRunProgress(Math.round(((idx + 1) / seedLogs.length) * 100));
        idx++;
      } else {
        clearInterval(scrollInterval);
        setIsExecuting(false);
      }
    }, 90);
  };

  return (
    <div className="flex flex-col gap-6 w-full relative min-h-full max-w-7xl mx-auto pb-16 px-4">
      {/* IMMUTABLE HEADER DECK */}
      <div className="glass-panel border-2 border-[#e840f3]/35 rounded-3xl p-6 relative overflow-hidden bg-black/85 shadow-2xl">
        <div className="absolute top-0 right-0 p-8 opacity-[0.03] pointer-events-none">
          <Terminal className="w-56 h-56 text-[#e840f3]" />
        </div>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 relative z-10 text-left">
          <div>
            <span className="text-amber-400 font-mono text-[9px] uppercase tracking-[0.3em] font-extrabold flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
              Verified NexoraX Architecture Protocol Node // 18-Sector Matrix Grid Active
            </span>
            <h1 className="text-2xl font-black font-sans tracking-tight text-white uppercase flex items-center gap-2.5">
              <Sliders className="text-cyan-400 w-7 h-7" />
              NexoraX 27 Workspace Tabs
            </h1>
            <p className="text-slate-400 font-mono text-xs mt-1.5 max-w-3xl leading-relaxed">
              In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds. 
              Deploy, calibrate, and execute deep-level node configurations across all 27 sub-categories in real-time.
            </p>
          </div>
          <div className="flex flex-col font-mono text-[9px] text-right text-slate-500 bg-[#02040b] border border-indigo-500/20 px-3.5 py-2 rounded-xl gap-1 min-w-[240px]">
            <div className="flex justify-between border-b border-white/5 pb-1"><span className="text-cyan-400 font-black">NEXORAX REMITTANCE:</span> <span className="text-slate-200">SecurePayment Verified Payment Configuration // Secure Billing Provider</span></div>
            <div className="flex justify-between border-b border-white/5 pb-1"><span className="text-cyan-400 font-black">IMPERIAL LOGS BACKUP:</span> <span className="text-slate-200">GDrive via CloudSync-02</span></div>
            <div className="flex justify-between"><span className="text-cyan-400 font-black">COMMS SECURITY LINE:</span> <span className="text-slate-200">08000 622827 Planet</span></div>
          </div>
        </div>
      </div>

      {/* MATRIX LAYOUT: SIDEBAR TABS & ACTIVE TERMINAL WORKSPACE */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* LEFT COLUMN: THE 27 SIDEBAR TABS (CategoryNavigationSidebar) */}
        <div className="lg:col-span-4 h-[750px] overflow-hidden flex flex-col">
          <CategoryNavigationSidebar 
            activeCategoryId={activeCategoryId} 
            onSelectCategory={(id) => {
              setActiveCategoryId(id);
              if (onSelectCategory) {
                onSelectCategory(id);
              }
            }} 
          />
        </div>

        {/* RIGHT COLUMN: ACTIVE INTERACTIVE DASHBOARD CONTAINER */}
        <div className="lg:col-span-8 flex flex-col gap-6 h-[750px] overflow-hidden">
          
          {/* HEADER CHIP */}
          <div className="glass-panel border border-indigo-500/10 rounded-2xl p-5 bg-[#040817]/95 flex flex-col shrink-0 text-left relative overflow-hidden">
            <div className="absolute top-0 right-0 -mr-6 -mt-6 p-10 opacity-5 pointer-events-none">
              <Cpu className="w-24 h-24 text-[#e840f3]" />
            </div>
            
            <span className="text-[#e840f3] font-mono text-[9px] uppercase tracking-widest font-black block">
              Active Category Endpoint Core: SEC-{activeCategory.id.toString().padStart(2, '0')} // TIER-1 POWER GRID
            </span>
            <h2 className="text-xl md:text-2xl font-black font-sans tracking-tight text-[#f1f5f9] mt-1 flex items-center gap-3">
              <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-ping"></span>
              {activeCategory.title} Workspace
            </h2>
            <p className="text-slate-400 font-mono text-[10px] uppercase tracking-wider mt-1.5 leading-relaxed">
              Interactive node console. Tweak inputs, configure custom routing triggers, and run live diagnostic testbed routines.
            </p>
            
            <div className="h-px bg-white/5 my-4"></div>

            {/* NODES SUB-TABS SELECTOR REGISTER */}
            <div className="space-y-2">
              <span className="text-[9px] font-mono text-indigo-400 font-bold uppercase tracking-widest block">
                Target Node Address Selection:
              </span>
              <div className="flex gap-2 overflow-x-auto pb-1 whitespace-nowrap custom-scrollbar">
                {activeCategory.nodes.map((node, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveNodeIndex(index)}
                    className={`px-3 py-1.5 rounded-lg font-mono text-[9px] uppercase tracking-wider border transition-all cursor-pointer ${
                      activeNodeIndex === index 
                        ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-300 font-bold' 
                        : 'bg-black/35 border-white/5 text-slate-500 hover:text-slate-300 hover:border-white/10'
                    }`}
                  >
                    {node}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* DYNAMIC BESPOKE INPUT CONTROLS FOR ALL 27 INDIVIDUAL TABS */}
          <div className="glass-panel border border-indigo-500/10 rounded-2xl p-5 bg-[#020510]/90 shrink-0 text-left">
            <span className="font-mono text-[9px] text-cyan-400 font-black uppercase tracking-widest flex items-center gap-2 mb-4">
              <Sliders className="w-3.5 h-3.5" />
              Dynamic Endpoint Parameters calibrator
            </span>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Category-Specific Form Inputs Rendered based on Active ID */}
              
              {/* RESTORATION CORE (1) */}
              {activeCategoryId === 1 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Secure DNS Handshaker Provider</label>
                    <select value={cat1Dns} onChange={(e) => setCat1Dns(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none focus:border-cyan-400">
                      <option value="Cloudflare Genesis Core">Cloudflare Genesis Core</option>
                      <option value="Route53 NexoraX Shield">Route53 NexoraX Shield</option>
                      <option value="Phantom IP DNS Array">Phantom IP DNS Array</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">IP Access Whitelist</label>
                    <input type="text" value={cat1Whitelist} onChange={(e) => setCat1Whitelist(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none focus:border-cyan-400" />
                  </div>
                  <div className="flex items-center gap-2 col-span-2 mt-2">
                    <input type="checkbox" checked={cat1KillNodes} onChange={(e) => setCat1KillNodes(e.target.checked)} id="cat1KillCheck" className="w-3.5 h-3.5 text-cyan-400 bg-black border-white/10 rounded focus:ring-0 cursor-pointer" />
                    <label htmlFor="cat1KillCheck" className="text-[9px] font-mono uppercase text-slate-400 select-none cursor-pointer">
                      Auto-Exterminate rogue domains, Indian brand impersonators, & copycat networks
                    </label>
                  </div>
                </>
              )}

              {/* IMPERIAL FORGE (2) */}
              {activeCategoryId === 2 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Ghost Equity Distribution Percentage</label>
                    <div className="flex items-center gap-3">
                      <input type="range" min="1" max="100" value={cat2Equity} onChange={(e) => setCat2Equity(Number(e.target.value))} className="w-full accent-[#e840f3] h-1 bg-black rounded" />
                      <span className="text-[10px] font-mono font-bold text-[#e840f3] w-10">{cat2Equity}%</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    <input type="checkbox" checked={cat2EspionageMask} onChange={(e) => setCat2EspionageMask(e.target.checked)} id="cat2EspCheck" className="w-3.5 h-3.5 text-cyan-400 bg-black border-white/10 rounded focus:ring-0 cursor-pointer" />
                    <label htmlFor="cat2EspCheck" className="text-[9px] font-mono uppercase text-slate-400 select-none cursor-pointer">
                      Enable Corporate Espionage Masking Protection
                    </label>
                  </div>
                </>
              )}

              {/* AI SYSTEMS (3) */}
              {activeCategoryId === 3 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Outreach Close Swarm Tracker Bots</label>
                    <input type="number" value={cat3BotsCount} onChange={(e) => setCat3BotsCount(Number(e.target.value))} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">NLP Target Decision Preset</label>
                    <select value={cat3NlpPreset} onChange={(e) => setCat3NlpPreset(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none">
                      <option value="VIP Close Outdrive">VIP Close Outdrive</option>
                      <option value="High-Friction Qualification Trigger">High-Friction Qualification Trigger</option>
                      <option value="Uncensored Singularity Matrix">Uncensored Singularity Matrix</option>
                    </select>
                  </div>
                </>
              )}

              {/* RECONNAISSANCE (4) */}
              {activeCategoryId === 4 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Target Competitor Hostname URL</label>
                    <input type="text" value={cat4TargetDomain} onChange={(e) => setCat4TargetDomain(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none focus:border-cyan-400" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Satellite Sweep Radar Power Level</label>
                    <select value={cat4RadarPower} onChange={(e) => setCat4RadarPower(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none">
                      <option value="LOW">LOW // Stealth Passive</option>
                      <option value="MEDIUM">MEDIUM // Active Area Survey</option>
                      <option value="MAX">MAX // Hyper-Frequency Intercept</option>
                    </select>
                  </div>
                </>
              )}

              {/* INTELLIGENCE (5) */}
              {activeCategoryId === 5 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">PsyOps Media Campaign Strategy</label>
                    <select value={cat5PsyOpsType} onChange={(e) => setCat5PsyOpsType(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none">
                      <option value="Memetic Viral Infiltration">Memetic Viral Infiltration</option>
                      <option value="Truth Extraction Paradigm">Truth Extraction Paradigm</option>
                      <option value="Macro Geopolitical Forecaster">Macro Geopolitical Forecaster</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Oracle Core Truth Coefficient ({cat5TruthIndex}%)</label>
                    <input type="range" min="50" max="100" value={cat5TruthIndex} onChange={(e) => setCat5TruthIndex(Number(e.target.value))} className="w-full accent-cyan-400 h-1 bg-black rounded" />
                  </div>
                </>
              )}

              {/* FRANCHISE COMMAND (6) */}
              {activeCategoryId === 6 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Territorial Domination Grid (Radius)</label>
                    <div className="flex items-center gap-3">
                      <input type="range" min="1" max="15" value={cat6ZoneRadius} onChange={(e) => setCat6ZoneRadius(Number(e.target.value))} className="w-full h-1 bg-black rounded accent-amber-400" />
                      <span className="text-[10px] font-mono text-slate-200 font-bold">{cat6ZoneRadius} mi</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Comp Monopoly Hegemony Index</label>
                    <select value={cat6MonopolyLevel} onChange={(e) => setCat6MonopolyLevel(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none">
                      <option value="LOCAL">LOCAL // Sub-district</option>
                      <option value="MEDIUM">MEDIUM // Regional cartel</option>
                      <option value="HIGH">HIGH // Total planetary monopoly</option>
                    </select>
                  </div>
                </>
              )}

              {/* GROWTH (7) */}
              {activeCategoryId === 7 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Core High-Conversion Ads Channel</label>
                    <input type="text" value={cat7AdChannel} onChange={(e) => setCat7AdChannel(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Neurological Brand Imprinting Score</label>
                    <input type="number" min="1" max="100" value={cat7NeuroScore} onChange={(e) => setCat7NeuroScore(Number(e.target.value))} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none" />
                  </div>
                </>
              )}

              {/* NETWORK INFRASTRUCTURE (8) */}
              {activeCategoryId === 8 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Nitrogen Cooling Grid Controller</label>
                    <div className="flex items-center gap-3">
                      <input type="range" min="10" max="100" value={cat8CoolingRate} onChange={(e) => setCat8CoolingRate(Number(e.target.value))} className="w-full h-1 bg-black accent-indigo-400 rounded" />
                      <span className="text-[10px] font-mono font-bold text-indigo-400">{cat8CoolingRate}%</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Quantum Distribution Key Variant</label>
                    <select value={cat8KeyStrength} onChange={(e) => setCat8KeyStrength(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none">
                      <option value="Quantum-QKD-9">Quantum-QKD-9 // Secure Ultra-Link</option>
                      <option value="Polymorphic AES-1024">Polymorphic AES-1024</option>
                    </select>
                  </div>
                </>
              )}

              {/* CREATIVE (9) */}
              {activeCategoryId === 9 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Synthetic Visual Theme Archetype</label>
                    <select value={cat9AestheticMode} onChange={(e) => setCat9AestheticMode(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none">
                      <option value="Cyberpunk Royal Ledger">Cyberpunk Royal Ledger</option>
                      <option value="Classic Swiss Minimalist Slate">Classic Swiss Minimalist Slate</option>
                      <option value="Galactic Brutalist Twilight">Galactic Brutalist Twilight</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Subconscious Vision Generation Prompt</label>
                    <input type="text" value={cat9Prompt} onChange={(e) => setCat9Prompt(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-[#e840f3] focus:outline-none" />
                  </div>
                </>
              )}

              {/* NETWORK COMMAND (10) */}
              {activeCategoryId === 10 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Board Override Security Key</label>
                    <input type="text" value={cat10OverrideKey} onChange={(e) => setCat10OverrideKey(e.target.value)} className="w-full bg-black border border-[#e840f3]/30 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">NexoraX Accounting Offshore Basin</label>
                    <select value={cat10TaxHaven} onChange={(e) => setCat10TaxHaven(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none">
                      <option value="Grand Cayman Isolated Vault B">Grand Cayman Isolated Vault B</option>
                      <option value="Swiss Crypto-NexoraX Domain">Swiss Crypto-NexoraX Domain</option>
                      <option value="Offshore Isle of Man Grid-8">Offshore Isle of Man Grid-8</option>
                    </select>
                  </div>
                </>
              )}

              {/* SYNDICATE HUB (11) */}
              {activeCategoryId === 11 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Cyber-Mercenary Target Profile</label>
                    <input type="text" value={cat11CovertTarget} onChange={(e) => setCat11CovertTarget(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Recon Smuggler Commission Index multiplier</label>
                    <div className="flex items-center gap-3">
                      <input type="range" min="100" max="300" value={cat11BribeMult} onChange={(e) => setCat11BribeMult(Number(e.target.value))} className="w-full h-1 bg-black accent-rose-400" />
                      <span className="text-[10px] font-mono text-slate-200 font-bold">{cat11BribeMult}%</span>
                    </div>
                  </div>
                </>
              )}

              {/* NEXORAX QUANTUM MATRIX (12) */}
              {activeCategoryId === 12 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Quantum Drift Index (deviation)</label>
                    <input type="number" step="0.0001" value={cat12TemporalDrift} onChange={(e) => setCat12TemporalDrift(Number(e.target.value))} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Timeline WarpHalo Threshold</label>
                    <div className="flex items-center gap-3">
                      <input type="range" min="50" max="100" step="0.1" value={cat12ProbabilityWarp} onChange={(e) => setCat12ProbabilityWarp(Number(e.target.value))} className="w-full h-1 bg-black accent-cyan-400 rounded" />
                      <span className="text-[10px] font-mono font-bold text-cyan-400">{cat12ProbabilityWarp}%</span>
                    </div>
                  </div>
                </>
              )}

              {/* OMNIVERSE INTENT GRID (13) */}
              {activeCategoryId === 13 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">NexoraX Meditation Frequency (Hz)</label>
                    <select value={cat13ChakraFreq} onChange={(e) => setCat13ChakraFreq(Number(e.target.value))} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none">
                      <option value="432">432 Hz // Cosmic Tuning</option>
                      <option value="528">528 Hz // DNA Transduction</option>
                      <option value="963">963 Hz // Crown Decryption</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Universal Telepathy Focal Target</label>
                    <input type="text" value={cat13ZenFocus} onChange={(e) => setCat13ZenFocus(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-amber-300 focus:outline-none" />
                  </div>
                </>
              )}

              {/* NATIONAL GOVERNANCE (14) */}
              {activeCategoryId === 14 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Thought Crime Sensitivity Flag threshold</label>
                    <div className="flex items-center gap-3">
                      <input type="range" min="1" max="50" value={cat14ThoughtCrimeThreshold} onChange={(e) => setCat14ThoughtCrimeThreshold(Number(e.target.value))} className="w-full h-1 bg-black accent-red-500 rounded" />
                      <span className="text-[10px] font-mono font-bold text-red-400">{cat14ThoughtCrimeThreshold}%</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Citizen Database Search filter Profile</label>
                    <input type="text" value={cat14CitizenDbFilter} onChange={(e) => setCat14CitizenDbFilter(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100" />
                  </div>
                </>
              )}

              {/* BUSINESS (15) */}
              {activeCategoryId === 15 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Precious Ore Asteroid Harvester Range</label>
                    <input type="text" value={cat15AsteroidSite} onChange={(e) => setCat15AsteroidSite(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Robotic Drone Logistics density</label>
                    <input type="number" min="10" max="100" value={cat15RoboticDensity} onChange={(e) => setCat15RoboticDensity(Number(e.target.value))} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100" />
                  </div>
                </>
              )}

              {/* EXPERIENCE CLOUD (16) */}
              {activeCategoryId === 16 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Cognitive Dopamine Micro-dosing dosage</label>
                    <div className="flex items-center gap-3">
                      <input type="range" min="10" max="500" value={cat16DopamineLevel} onChange={(e) => setCat16DopamineLevel(Number(e.target.value))} className="w-full h-1 bg-black accent-pink-500 rounded" />
                      <span className="text-[10px] font-mono font-bold text-pink-400">{cat16DopamineLevel} ug</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Biometric Brainwave Resonance Format</label>
                    <input type="text" value={cat16HapticFrequency} onChange={(e) => setCat16HapticFrequency(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100" />
                  </div>
                </>
              )}

              {/* REVENUE OPS (17) */}
              {activeCategoryId === 17 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Galactic Portal Transit destination Corridor</label>
                    <input type="text" value={cat17PortalTarget} onChange={(e) => setCat17PortalTarget(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Temporal Trading arbitrage spread</label>
                    <input type="number" step="0.1" value={cat17ArbitrageMargin} onChange={(e) => setCat17ArbitrageMargin(Number(e.target.value))} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100" />
                  </div>
                </>
              )}

              {/* POS SYSTEMS (18) */}
              {activeCategoryId === 18 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Active Till checkout Station ID</label>
                    <input type="text" value={cat18StationIndex} onChange={(e) => setCat18StationIndex(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Retinal Checkout currency multiplier</label>
                    <select value={cat18PulseMultiplier} onChange={(e) => setCat18PulseMultiplier(Number(e.target.value))} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100">
                      <option value="1.00">1.00x Nominal Index</option>
                      <option value="1.05">1.05x Premium Levy</option>
                      <option value="1.20">1.20x Boardroom Surge</option>
                    </select>
                  </div>
                </>
              )}

              {/* ACCOUNTING (CHRONOS) (19) */}
              {activeCategoryId === 19 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Forensic Audit Timeline loop replication</label>
                    <input type="number" value={cat19AuditLoops} onChange={(e) => setCat19AuditLoops(Number(e.target.value))} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100" />
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    <input type="checkbox" checked={cat19RibaNeutralizer} onChange={(e) => setCat19RibaNeutralizer(e.target.checked)} id="cat19RibaCheck" className="w-3.5 h-3.5 text-cyan-400 bg-black border-white/10 rounded focus:ring-0 cursor-pointer" />
                    <label htmlFor="cat19RibaCheck" className="text-[9px] font-mono uppercase text-slate-400 select-none cursor-pointer">
                      Enforce strict Islamic Finance Riba-Neutralizer algorithms
                    </label>
                  </div>
                </>
              )}

              {/* JUDICIARY & LAW (20) */}
              {activeCategoryId === 20 && (
                <>
                  <div className="space-y-1.5 col-span-2">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Target Rogue Entity for Imperial Indictment</label>
                    <input type="text" value={cat20IndictedTitle} onChange={(e) => setCat20IndictedTitle(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none" />
                  </div>
                  <div className="space-y-1.5 col-span-2">
                    <label className="text-[9px] font-mono uppercase text-slate-400">NexoraX Tribunal Sanction Severity</label>
                    <select value={cat20GavelSanctionRating} onChange={(e) => setCat20GavelSanctionRating(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100">
                      <option value="NexoraX Disqualification">NexoraX Disqualification // Force Domain Blackout</option>
                      <option value="Asset Stripping Penalties">Asset Stripping Penalties // Liquidate Assets</option>
                      <option value="Minority Report Prison Isolation">Minority Report Prison Isolation</option>
                    </select>
                  </div>
                </>
              )}

              {/* TALENT HUB (21) */}
              {activeCategoryId === 21 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">National Insurance Number for NexoraX billing records</label>
                    <input type="text" value={cat21NiNumber} onChange={(e) => setCat21NiNumber(e.target.value)} className="w-full bg-black border border-[#e840f3]/30 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Sponsorship Enterprise Standards Route</label>
                    <select value={cat21SponsorshipOption} onChange={(e) => setCat21SponsorshipOption(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100">
                      <option value="Enterprise Standards Strike Architect Route">Enterprise Standards Strike Architect Route (Operations)</option>
                      <option value="Executive Principal Private Secretary">Executive Principal Private Secretary (Claire/Helen)</option>
                    </select>
                  </div>
                </>
              )}

              {/* NEXORAX INFRASTRUCTURE (22) */}
              {activeCategoryId === 22 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Platform Stellar Core construct Unit ID</label>
                    <input type="text" value={cat22PlatformIndex} onChange={(e) => setCat22PlatformIndex(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Planetary Shield geometry Strength</label>
                    <div className="flex items-center gap-3">
                      <input type="range" min="10" max="100" value={cat22ShieldStrength} onChange={(e) => setCat22ShieldStrength(Number(e.target.value))} className="w-full h-1 bg-black accent-cyan-400 rounded" />
                      <span className="text-[10px] font-mono font-bold text-cyan-400">{cat22ShieldStrength}%</span>
                    </div>
                  </div>
                </>
              )}

              {/* THE PHILANTHROPIC VAULT (23) */}
              {activeCategoryId === 23 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Direct Zakat Remittance Charity account</label>
                    <select value={cat23CharityTarget} onChange={(e) => setCat23CharityTarget(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100">
                      <option value="Dunfermline Local Regional Fund">Charity 1: Dunfermline Local Regional Fund</option>
                      <option value="Global Access To Technology Relief">Charity 2: Global Access To Technology Relief</option>
                      <option value="UK Disabled Talent Advocacy Trust">Charity 3: UK Disabled Talent Advocacy Trust</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Mission Placement focus Project</label>
                    <input type="text" value={cat23ProjectFocus} onChange={(e) => setCat23ProjectFocus(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100" />
                  </div>
                </>
              )}

              {/* WEALTH & NEXORAXTY (24) */}
              {activeCategoryId === 24 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Family Office Secure Vault Key-Pass</label>
                    <input type="text" value={cat24FamilyOfficeKey} onChange={(e) => setCat24FamilyOfficeKey(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Godhood creator privilege Tier</label>
                    <select value={cat24GodhoodTier} onChange={(e) => setCat24GodhoodTier(e.target.value)} className="w-full bg-black border border-[#e840f3]/30 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100">
                      <option value="VIP Singularity Level">VIP Singularity Level (All privileges unlocked)</option>
                      <option value="Apex NexoraX Throne Room">Apex NexoraX Throne Room</option>
                    </select>
                  </div>
                </>
              )}

              {/* BRANDING ENGINE (25) */}
              {activeCategoryId === 25 && (
                <>
                  <div className="space-y-1.5 col-span-2">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Target Google SEO metadata Keywords</label>
                    <input type="text" value={cat25SeoKeywords} onChange={(e) => setCat25SeoKeywords(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100" />
                  </div>
                  <div className="space-y-1.5 col-span-2">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Competitive SEO Rank strategy</label>
                    <select value={cat25RankTarget} onChange={(e) => setCat25RankTarget(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100">
                      <option value="Top #1 Position Guaranteed">Top #1 Position Guaranteed (Deflect Indian Duplicators)</option>
                      <option value="Mass Organic Omniversal Hegemony">Mass Organic Omniversal Hegemony</option>
                    </select>
                  </div>
                </>
              )}

              {/* CRM & SLIDE DECK (26) */}
              {activeCategoryId === 26 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Active whale lead Contact Name</label>
                    <input type="text" value={cat26LeadName} onChange={(e) => setCat2LeadName(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none focus:border-[#e840f3]" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Slide followup Automation Cadence</label>
                    <select value={cat26FollowUpMode} onChange={(e) => setCat26FollowUpMode(e.target.value)} className="w-full bg-black border border-white/10 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100">
                      <option value="Ultra-High Cadence Automated SMS">Ultra-High Cadence Automated SMS (0.8s monitoring)</option>
                      <option value="Discrete Boardroom Concierge Email">Discrete Boardroom Concierge Email</option>
                    </select>
                  </div>
                </>
              )}

              {/* AI AUTOMATION (27) */}
              {activeCategoryId === 27 && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Target Core Node processing CPU engine</label>
                    <select value={cat27TargetEngine} onChange={(e) => setCat27TargetEngine(e.target.value)} className="w-full bg-black border border-[#e840f3]/30 rounded-xl px-3 py-2 text-[10px] font-mono text-slate-100 focus:outline-none">
                      <option value="API_KEY_ENGINE">API_KEY_ENGINE</option>
                      <option value="AI_ENGINE">AI_ENGINE</option>
                      <option value="CODE_ENGINE">CODE_ENGINE</option>
                      <option value="AI_GENERATOR">AI_GENERATOR</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-mono uppercase text-slate-400">Ephemeral serverless micro-container instances</label>
                    <div className="flex items-center gap-3">
                      <input type="range" min="10" max="300" value={cat27ContainerScaling} onChange={(e) => setCat27ContainerScaling(Number(e.target.value))} className="w-full h-1 bg-black accent-cyan-400" />
                      <span className="text-[10px] font-mono font-bold text-cyan-400">{cat27ContainerScaling}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* SHARED SIMULATION VALUES */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 pt-4 border-t border-white/5">
              <div className="space-y-1">
                <span className="text-[8px] font-mono uppercase text-slate-500 block">Processing Traffic</span>
                <span className="text-xs font-bold text-slate-300 font-mono flex items-center gap-1.5">
                  <Activity className="w-3.5 h-3.5 text-cyan-400" />
                  {procLoad} req/sec
                </span>
                <input type="range" min="100" max="1500" value={procLoad} onChange={(e) => setProcLoad(Number(e.target.value))} className="w-full h-0.5 bg-black appearance-none" />
              </div>
              <div className="space-y-1">
                <span className="text-[8px] font-mono uppercase text-slate-500 block">Simulated Flow</span>
                <span className="text-xs font-bold text-[#e840f3] font-mono">
                  £{txVolume.toLocaleString()}
                </span>
                <input type="range" min="5000" max="150000" step="5000" value={txVolume} onChange={(e) => setTxVolume(Number(e.target.value))} className="w-full h-0.5 bg-black appearance-none" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={isGdriveSync} onChange={(e) => setIsGdriveSync(e.target.checked)} id="isGdriveSyncCheck" className="w-3 h-3 text-[#e840f3] bg-black border-white/10 rounded cursor-pointer" />
                <label htmlFor="isGdriveSyncCheck" className="text-[8px] font-mono text-slate-400 uppercase select-none cursor-pointer">Replicate GDrive NexoraX Resource Library</label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={isSecurePaymentProviderAccessActive} onChange={(e) => setIsSecurePaymentProviderAccessActive(e.target.checked)} id="isSecurePaymentProviderCheck" className="w-3 h-3 text-cyan-400 bg-black border-white/10 rounded cursor-pointer" />
                <label htmlFor="isSecurePaymentProviderCheck" className="text-[8px] font-mono text-slate-400 uppercase select-none cursor-pointer">NexoraX billing records Secure Payment Provider Anchor</label>
              </div>
            </div>
          </div>

          {/* ACTIVE NEXORAX BILLING LEDGER */}
          <div className="glass-panel border border-[#e840f3]/15 rounded-2xl p-4 bg-gradient-to-b from-[#02050f]/80 to-[#04091a] shrink-0 text-left">
            <span className="font-mono text-[9px] text-[#10b981] font-black uppercase tracking-widest flex items-center gap-1.5 mb-2">
              <Coins className="w-4 h-4" />
              Corporate Revenue Ledger Audit (VAT-NexoraX-SLA Verified Route)
            </span>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-black/40 p-3 rounded-xl">
              <div>
                <span className="text-[8px] font-mono text-slate-500 uppercase block">Gross Input Flux</span>
                <span className="text-sm font-black font-sans text-slate-100">£{grossInVal.toLocaleString()}</span>
              </div>
              <div className="border-l border-white/5 pl-3">
                <span className="text-[8px] font-mono text-cyan-400 uppercase block">1% Perpetual Zakat</span>
                <span className="text-sm font-black font-sans text-cyan-400">£{levyVal.toLocaleString()}</span>
              </div>
              <div className="border-l border-white/5 pl-3">
                <span className="text-[8px] font-mono text-amber-500 uppercase block">20% NexoraX Tax</span>
                <span className="text-sm font-black font-sans text-amber-500">£{lifetimeTaxVal.toLocaleString()}</span>
              </div>
              <div className="border-l border-white/5 pl-3">
                <span className="text-[8px] font-mono text-emerald-400 uppercase block">Net Remitted SecurePayment</span>
                <span className="text-sm font-black font-sans text-emerald-400">£{netSecurePaymentVal.toLocaleString()}</span>
              </div>
            </div>

            {/* SPLITS RATIOS LIST */}
            <div className="grid grid-cols-3 gap-2 mt-2 font-mono text-[8.5px] uppercase text-slate-400">
              <div className="bg-black/40 px-2 py-1.5 rounded text-center border border-white/5">
                <span className="text-slate-500">Charity 1 (Local): </span>
                <strong className="text-slate-300">£{charitySplitsVal.toFixed(2)}</strong>
              </div>
              <div className="bg-black/40 px-2 py-1.5 rounded text-center border border-white/5">
                <span className="text-slate-500">Charity 2 (Global): </span>
                <strong className="text-slate-300">£{charitySplitsVal.toFixed(2)}</strong>
              </div>
              <div className="bg-black/40 px-2 py-1.5 rounded text-center border border-white/5">
                <span className="text-slate-500">Charity 3 (UK Trust): </span>
                <strong className="text-slate-300">£{charitySplitsVal.toFixed(2)}</strong>
              </div>
            </div>
          </div>

          {/* ACTIVE TERMINAL OUTPUT CONSOLE */}
          <div className="flex-1 glass-panel border border-indigo-500/10 rounded-2xl bg-black/95 p-4 font-mono text-[10px] flex flex-col relative overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-2 mb-2 shrink-0">
              <div className="flex items-center gap-1.5">
                <Terminal className="text-cyan-400 w-3.5 h-3.5" />
                <span className="text-slate-300 uppercase tracking-wider font-mono font-bold">NEXORAX WORKSPACE FEEDBACK</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${isExecuting ? 'bg-amber-400 animate-pulse' : 'bg-emerald-500 animate-pulse'}`}></span>
                <span className="text-[8px] text-slate-500 uppercase tracking-widest">{isExecuting ? 'EXECUTING DIRECTIVE' : 'STAGING_STANDBY'}</span>
              </div>
            </div>

            {/* LIVE CONSOLE LOGS scrollable area */}
            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-1.5 pr-2 select-text text-left">
              {logFeed.length > 0 ? (
                logFeed.map((log, index) => {
                  let isPre = log.includes("Allah");
                  let isHeader = log.includes("---");
                  let isErr = log.includes("[COMPLIANCE-ALERT]") || log.includes("leak") || log.includes("WARNING");
                  let isOk = log.includes("Settlement") || log.includes("verified") || log.includes("[COMPLETE]") || log.includes("Sync");

                  return (
                    <div 
                      key={index} 
                      className={`${
                        isPre ? 'text-amber-400 font-semibold italic border-b border-amber-500/10 pb-1.5 mb-1.5 glow-text-amber' : 
                        isHeader ? 'text-cyan-400 border-t border-indigo-500/10 pt-1.5 mt-1.5 font-extrabold tracking-widest' : 
                        isErr ? 'text-rose-400 font-bold' : 
                        isOk ? 'text-emerald-400 font-bold' : 'text-slate-400'
                      }`}
                    >
                      {log}
                    </div>
                  );
                })
              ) : (
                <div className="text-slate-700 italic text-center py-20 uppercase tracking-wider">
                  [ CONSOLE STANDBY - PRESS THE CONTROLLER SWITCH TO FIRE DIRECTIVE ]
                </div>
              )}
              <div ref={logFeedEndRef} />
            </div>

            {/* RUN SWITCH TERMINAL CONTROL */}
            <div className="border-t border-white/10 pt-3 mt-2 shrink-0 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                {isExecuting && (
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-3.5 h-3.5 text-[#e840f3] animate-spin" />
                    <span className="text-[9px] text-[#e840f3] font-bold uppercase">Processing Engine: {runProgress}% synchronized</span>
                  </div>
                )}
              </div>

              <button
                onClick={triggerCoreNexoraXSimulation}
                disabled={isExecuting}
                className={`px-6 py-2.5 rounded-lg border font-mono font-bold text-[10px] uppercase tracking-wider transition-all flex items-center gap-2 cursor-pointer ${
                  isExecuting 
                    ? 'border-slate-800 text-slate-600 bg-black/40 cursor-not-allowed' 
                    : 'border-[#e840f3] text-[#e840f3] hover:border-fuchsia-300 bg-fuchsia-950/20 hover:bg-fuchsia-900/10 hover:shadow-[0_0_15px_rgba(232,64,243,0.3)]'
                }`}
              >
                <Play className="w-3.5 h-3.5" />
                Fire NexoraX Target Operation
              </button>
            </div>
          </div>

        </div>

      </div>

    </div>
  );
}
